import { Divider, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { formatHoursDurationCompact } from "@src/appV2/redesign/utils/formatHoursDurationCompact";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";

import { type TimesheetEntry } from "../hooks/useTimesheetEntry";
import { calculateTimesheetWorkedHours } from "../utils/timesheetFormatUtils";
import { TimesheetTimeEntryRow } from "./TimesheetTimeEntryRow";

interface TimesheetTimeEntriesProps {
  /** Hide pencil icons when editing should be disabled (e.g., timekeeping integrations) */
  hidePencilIcons: boolean;
  disabled?: boolean;
  displayTimesheet: TimesheetEntry;
  workplaceTimezone: string;
  onEditClockIn: () => void;
  onEditBreak: () => void;
  onEditClockOut: () => void;
  formatBreakDisplay: () => string;
  /** Pass the original time when clock-in has been adjusted from the recorded value */
  originalClockInTime?: Date;
  /**
   * Pass the original time when clock-out has been adjusted from the recorded value.
   * Reserved for future use — clock-out adjustments are not yet wired up by callers,
   * but this prop is in place for the upcoming story.
   */
  originalClockOutTime?: Date;
  /** Pass the original formatted break string when break has been adjusted */
  originalBreakDisplay?: string;
  /**
   * Pass the original shift timesheet (hcp_clock_in/out) to show a Verified/Pending
   * hours breakdown below the Total row when the worker is claiming more time than recorded.
   */
  verifiedTimesheet?: TimesheetEntry;
}

export function TimesheetTimeEntries(props: TimesheetTimeEntriesProps) {
  const {
    hidePencilIcons,
    disabled = false,
    displayTimesheet,
    workplaceTimezone,
    onEditClockIn,
    onEditBreak,
    onEditClockOut,
    formatBreakDisplay,
    originalClockInTime,
    originalClockOutTime,
    originalBreakDisplay,
    verifiedTimesheet,
  } = props;

  function formatWorkedHours(hours: number): string {
    return formatHoursDurationCompact(hours, {
      areLeadingZeroesAllowed: false,
      omitZeroMinutes: true,
    });
  }

  function formatWorkplaceTime(date: Date): string {
    return formatTimeWithTimezone(date, workplaceTimezone, false);
  }

  const adjustedHours = calculateTimesheetWorkedHours(displayTimesheet);
  const verifiedHours = isDefined(verifiedTimesheet)
    ? calculateTimesheetWorkedHours(verifiedTimesheet)
    : undefined;
  const pendingHours =
    isDefined(verifiedHours) && adjustedHours > verifiedHours
      ? adjustedHours - verifiedHours
      : undefined;

  return (
    <Stack spacing={4}>
      <TimesheetTimeEntryRow
        iconType="clock"
        label="Clock in"
        originalValue={originalClockInTime ? formatWorkplaceTime(originalClockInTime) : undefined}
      >
        <Button
          variant="outlined"
          size="small"
          disabled={disabled}
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditClockIn}
        >
          {formatWorkplaceTime(displayTimesheet.clockInTime)}
        </Button>
      </TimesheetTimeEntryRow>

      <TimesheetTimeEntryRow iconType="break" label="Break" originalValue={originalBreakDisplay}>
        <Button
          variant="outlined"
          size="small"
          disabled={disabled}
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditBreak}
        >
          {formatBreakDisplay()}
        </Button>
      </TimesheetTimeEntryRow>

      <TimesheetTimeEntryRow
        iconType="clock"
        label="Clock out"
        originalValue={originalClockOutTime ? formatWorkplaceTime(originalClockOutTime) : undefined}
      >
        <Button
          variant="outlined"
          size="small"
          disabled={disabled}
          endIconType={hidePencilIcons ? undefined : "pencil"}
          onClick={onEditClockOut}
        >
          {formatWorkplaceTime(displayTimesheet.clockOutTime)}
        </Button>
      </TimesheetTimeEntryRow>

      <Divider />

      {isDefined(verifiedTimesheet) ? (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Text bold sx={{ color: (theme) => theme.palette.text.tertiary }}>
              Verified hours
            </Text>
            <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
              {formatWorkedHours(verifiedHours ?? 0)}
            </Text>
          </Stack>
          {isDefined(pendingHours) && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Text bold sx={{ color: (theme) => theme.palette.text.tertiary }}>
                Pending approval
              </Text>
              <Text bold sx={{ color: (theme) => theme.palette.success.main }}>
                +{formatWorkedHours(pendingHours)}
              </Text>
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Text bold variant="h5" sx={{ color: (theme) => theme.palette.text.tertiary }}>
            Total
          </Text>
          <Text bold variant="h5" sx={{ color: (theme) => theme.palette.success.main }}>
            {formatWorkedHours(calculateTimesheetWorkedHours(displayTimesheet))}
          </Text>
        </Stack>
      )}
    </Stack>
  );
}
